import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "../../App";

const isDev = process.env.NODE_ENV === 'development';
const log = (...args) => { if (isDev) console.log(...args); };
const logWarn = (...args) => { if (isDev) console.warn(...args); };
const logError = (...args) => { if (isDev) console.error(...args); };

export const VOICE_STATUS = {
  IDLE: 'idle',
  LISTENING: 'listening',
  THINKING: 'thinking',
  SPEAKING: 'speaking',
  MUTED: 'muted'
};

export function useVoicePipeline() {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [voiceStatus, setVoiceStatus] = useState(VOICE_STATUS.IDLE);
  const [resistanceLevel, setResistanceLevel] = useState(7);
  const [turnCount, setTurnCount] = useState(0);
  const [sessionTimer, setSessionTimer] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState("");
  const [wsConnected, setWsConnected] = useState(false);

  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const recognitionRef = useRef(null);
  const timerRef = useRef(null);
  const wsRef = useRef(null);
  const audioChunksRef = useRef([]);
  const isMutedRef = useRef(false);
  const voiceStatusRef = useRef(VOICE_STATUS.IDLE);
  const sessionRef = useRef(null);
  const selectedLanguageRef = useRef("hinglish");

  const updateVoiceStatus = (status) => {
    voiceStatusRef.current = status;
    setVoiceStatus(status);
  };

  const updateMuted = (muted) => {
    isMutedRef.current = muted;
    setIsMuted(muted);
  };

  const updateSession = (sess) => {
    sessionRef.current = sess;
    setSession(sess);
  };

  const setLanguageRef = (lang) => { selectedLanguageRef.current = lang; };

  useEffect(() => {
    return () => {
      disconnectWebSocket();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (session) {
      timerRef.current = setInterval(() => setSessionTimer(prev => prev + 1), 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [session]);

  // ========= WebSocket =========

  const connectWebSocket = useCallback(async () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    let wsToken = '';
    try {
      const { data } = await axios.get(`${API}/auth/ws-token`);
      wsToken = data.token;
      log("[Pipeline] WS token acquired");
    } catch (e) {
      logError('[Pipeline] Failed to get WS token via API, using stored token:', e);
      wsToken = localStorage.getItem("access_token") || '';
    }
    const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/conversation${wsToken ? `?token=${wsToken}` : ''}`;
    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => { log('[Pipeline] WebSocket connected'); setWsConnected(true); };
    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        audioChunksRef.current.push(new Uint8Array(event.data));
      } else {
        try { handleWsMessageDirect(JSON.parse(event.data)); } catch (e) { logError('[Pipeline] WS parse error:', e); }
      }
    };
    ws.onclose = (e) => {
      log('[Pipeline] WebSocket closed:', e.code);
      setWsConnected(false);
      if (sessionRef.current) setTimeout(() => connectWebSocket(), 2000);
    };
    ws.onerror = (e) => { logError('[Pipeline] WebSocket error:', e); };
    wsRef.current = ws;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const disconnectWebSocket = () => {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    setWsConnected(false);
  };

  const sendWsMessage = (data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
      log("[Pipeline] WS message sent:", data.type);
      return true;
    }
    return false;
  };

  const handleWsMessageDirect = (data) => {
    log("[Pipeline] WS received:", data.type);
    switch (data.type) {
      case 'connected': break;
      case 'thinking': updateVoiceStatus(VOICE_STATUS.THINKING); break;
      case 'ai_text':
        log("[Pipeline] AI response received:", data.text?.substring(0, 50));
        setMessages(prev => [...prev, { role: 'assistant', content: data.text, timestamp: new Date().toISOString() }]);
        setResistanceLevel(data.resistance_level);
        setTurnCount(data.turn_count);
        break;
      case 'audio_start':
        audioChunksRef.current = [];
        updateVoiceStatus(VOICE_STATUS.SPEAKING);
        break;
      case 'audio_complete': playAccumulatedAudioDirect(data.interrupted); break;
      case 'interrupted':
        stopAudio();
        updateVoiceStatus(VOICE_STATUS.IDLE);
        if (!isMutedRef.current) startListeningDirect();
        break;
      case 'error':
        logError('[Pipeline] Server error:', data.message);
        updateVoiceStatus(VOICE_STATUS.IDLE);
        break;
      default: break;
    }
  };

  // ========= Audio Playback =========

  const playAccumulatedAudioDirect = (wasInterrupted) => {
    const chunks = audioChunksRef.current;
    audioChunksRef.current = [];
    if (wasInterrupted || chunks.length === 0) {
      updateVoiceStatus(VOICE_STATUS.IDLE);
      if (!isMutedRef.current) startListeningDirect();
      return;
    }
    const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
    const merged = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) { merged.set(chunk, offset); offset += chunk.length; }
    const blob = new Blob([merged], { type: 'audio/mpeg' });
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audioRef.current = audio;
    log(`[Pipeline] Playing TTS audio (${chunks.length} chunks, ${totalLength} bytes)`);
    const onDone = () => {
      URL.revokeObjectURL(url); audioRef.current = null;
      updateVoiceStatus(VOICE_STATUS.IDLE);
      if (!isMutedRef.current) setTimeout(() => startListeningDirect(), 300);
    };
    audio.onended = () => { log("[Pipeline] TTS audio ended"); onDone(); };
    audio.onerror = () => { logError("[Pipeline] Audio playback error"); onDone(); };
    updateVoiceStatus(VOICE_STATUS.SPEAKING);
    audio.play().catch(() => onDone());
  };

  const playBase64AudioDirect = (base64Audio) => {
    updateVoiceStatus(VOICE_STATUS.SPEAKING);
    const audio = new Audio(`data:audio/mpeg;base64,${base64Audio}`);
    audioRef.current = audio;
    const onDone = () => {
      audioRef.current = null;
      updateVoiceStatus(VOICE_STATUS.IDLE);
      if (!isMutedRef.current) setTimeout(() => startListeningDirect(), 300);
    };
    audio.onended = () => { log("[Pipeline] Initial audio ended"); onDone(); };
    audio.onerror = onDone;
    audio.play().catch(onDone);
  };

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; audioRef.current = null; }
    audioChunksRef.current = [];
  };

  // ========= Speech Recognition =========

  const startListeningDirect = () => {
    if (!sessionRef.current || voiceStatusRef.current === VOICE_STATUS.THINKING || isMutedRef.current) return;
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) return;

    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch (e) { logWarn("[Pipeline] STT stop warning:", e.message); } recognitionRef.current = null; }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    const lang = sessionRef.current?.selected_language || selectedLanguageRef.current;
    recognition.lang = lang === 'en' ? 'en-IN' : 'hi-IN';

    let finalTranscript = '';
    let interimBuffer = '';
    let silenceTimer = null;

    recognition.onstart = () => { log("[Pipeline] STT started"); updateVoiceStatus(VOICE_STATUS.LISTENING); setCurrentTranscript(""); };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript + ' ';
        else interimTranscript += event.results[i][0].transcript;
      }
      interimBuffer = interimTranscript;
      setCurrentTranscript(finalTranscript + interimTranscript);
      if (silenceTimer) clearTimeout(silenceTimer);
      const textToCheck = finalTranscript.trim() || interimBuffer.trim();
      if (textToCheck.length > 0) {
        silenceTimer = setTimeout(() => {
          const textToSend = finalTranscript.trim() || interimBuffer.trim();
          if (textToSend.length > 0) {
            log("[Pipeline] Silence detected, sending:", textToSend.substring(0, 60));
            try { recognition.stop(); } catch (e) { logWarn("[Pipeline] STT stop warning:", e.message); }
            sendVoiceMessageDirect(textToSend);
            finalTranscript = ''; interimBuffer = '';
          }
        }, 1500);
      }
    };

    recognition.onerror = (event) => {
      log("[Pipeline] STT error:", event.error);
      if (event.error !== 'no-speech' && event.error !== 'aborted') updateVoiceStatus(VOICE_STATUS.IDLE);
    };

    recognition.onend = () => {
      log("[Pipeline] STT ended");
      if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
      const pendingText = finalTranscript.trim() || interimBuffer.trim();
      if (pendingText.length > 0 && voiceStatusRef.current === VOICE_STATUS.LISTENING) {
        sendVoiceMessageDirect(pendingText); finalTranscript = ''; interimBuffer = ''; return;
      }
      if (sessionRef.current && !isMutedRef.current && voiceStatusRef.current === VOICE_STATUS.LISTENING) {
        setTimeout(() => {
          if (sessionRef.current && !isMutedRef.current && voiceStatusRef.current !== VOICE_STATUS.THINKING && voiceStatusRef.current !== VOICE_STATUS.SPEAKING) startListeningDirect();
        }, 100);
      }
    };

    recognitionRef.current = recognition;
    try { recognition.start(); } catch (e) {
      logWarn("[Pipeline] STT start retry:", e.message);
      setTimeout(() => { try { recognition.start(); } catch (e2) { logError("[Pipeline] STT start failed:", e2.message); updateVoiceStatus(VOICE_STATUS.IDLE); } }, 200);
    }
  };

  const stopListeningDirect = () => {
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch (e) { logWarn("[Pipeline] STT stop warning:", e.message); } recognitionRef.current = null; }
    updateVoiceStatus(VOICE_STATUS.IDLE);
    setCurrentTranscript("");
  };

  // ========= Voice Message Sending =========

  const sendVoiceMessageDirect = (text) => {
    const sess = sessionRef.current;
    if (!text.trim() || !sess) return;
    if (voiceStatusRef.current === VOICE_STATUS.THINKING) return;
    log("[Pipeline] Sending voice message:", text.substring(0, 60));
    setCurrentTranscript("");
    setMessages(prev => [...prev, { role: "user", content: text, timestamp: new Date().toISOString() }]);
    const sent = sendWsMessage({ type: "user_message", session_id: sess.session_id, message: text });
    if (!sent) {
      sendVoiceMessageHTTPDirect(text, sess);
    } else {
      updateVoiceStatus(VOICE_STATUS.THINKING);
    }
  };

  const sendVoiceMessageHTTPDirect = async (text, sess) => {
    updateVoiceStatus(VOICE_STATUS.THINKING);
    try {
      const { data } = await axios.post(`${API}/conversation/respond`, { session_id: sess.session_id, message: text });
      setMessages(prev => [...prev, { role: "assistant", content: data.ai_response, timestamp: new Date().toISOString() }]);
      setResistanceLevel(data.resistance_level);
      setTurnCount(data.turn_count);
      if (data.audio_base64) playBase64AudioDirect(data.audio_base64);
      else { updateVoiceStatus(VOICE_STATUS.IDLE); if (!isMutedRef.current) startListeningDirect(); }
    } catch (e) {
      logError("[Pipeline] HTTP fallback failed:", e);
      updateVoiceStatus(VOICE_STATUS.IDLE);
      if (!isMutedRef.current) startListeningDirect();
    }
  };

  // ========= Public Controls =========

  const toggleMute = () => {
    if (isMutedRef.current) {
      log("[Pipeline] Unmuting");
      updateMuted(false);
      if (voiceStatusRef.current !== VOICE_STATUS.SPEAKING && voiceStatusRef.current !== VOICE_STATUS.THINKING) startListeningDirect();
    } else {
      log("[Pipeline] Muting");
      updateMuted(true);
      stopListeningDirect();
      updateVoiceStatus(VOICE_STATUS.MUTED);
    }
  };

  const handleMicButtonClick = () => {
    if (voiceStatusRef.current === VOICE_STATUS.SPEAKING) {
      stopAudio(); sendWsMessage({ type: "interrupt" }); updateVoiceStatus(VOICE_STATUS.IDLE);
      if (!isMutedRef.current) startListeningDirect();
    } else if (voiceStatusRef.current === VOICE_STATUS.LISTENING) {
      toggleMute();
    } else if (isMutedRef.current || voiceStatusRef.current === VOICE_STATUS.MUTED) {
      toggleMute();
    } else if (voiceStatusRef.current === VOICE_STATUS.IDLE) {
      startListeningDirect();
    }
  };

  const startConversation = async (scenarioId, personaId, language) => {
    updateVoiceStatus(VOICE_STATUS.THINKING);
    const { data } = await axios.post(`${API}/conversation/start`, {
      scenario_id: scenarioId,
      persona_id: personaId || undefined,
      selected_language: language
    });
    updateSession(data);
    setMessages([{ role: "assistant", content: data.initial_message, timestamp: new Date().toISOString() }]);
    setResistanceLevel(data.resistance_level);
    setTurnCount(1);
    setSessionTimer(0);
    connectWebSocket();
    log("[Pipeline] Conversation started, session:", data.session_id);
    if (data.audio_base64) playBase64AudioDirect(data.audio_base64);
    else { updateVoiceStatus(VOICE_STATUS.IDLE); setTimeout(() => startListeningDirect(), 500); }
    return data;
  };

  const endConversation = async (navigateFn) => {
    stopListeningDirect(); stopAudio(); disconnectWebSocket();
    if (timerRef.current) clearInterval(timerRef.current);
    const sess = sessionRef.current;
    if (!sess) return;
    try {
      await axios.post(`${API}/conversation/end`, { session_id: sess.session_id });
      navigateFn(`/evaluation/${sess.session_id}`);
    } catch (e) {
      logError("[Pipeline] Failed to end conversation:", e);
    }
  };

  return {
    session, messages, voiceStatus, resistanceLevel, turnCount, sessionTimer,
    isMuted, currentTranscript, wsConnected, messagesEndRef,
    startConversation, endConversation, toggleMute, handleMicButtonClick,
    setLanguageRef, VOICE_STATUS
  };
}
